import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import BookingModal from '../../components/BookingModal';

const MountGahingaClimb = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Mount Gahinga Volcano Climb
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Hike to the swamp-filled crater of the "Pile of Stones" in Mgahinga Gorilla National Park
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(52 reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-droplet" className="w-4 h-4 mr-2" />
                Crater Swamp Lake
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-mountain" className="w-4 h-4 mr-2" />
                3,474m Summit
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Hike
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                The Gentlest of Mgahinga's Volcanoes
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Mount Gahinga, whose name translates to "pile of stones" in the local Rufumbira language, offers the most accessible summit experience among Mgahinga's three volcanoes. At 3,474 meters, it is lower than Muhavura and less technically demanding than Sabinyo, making it perfect for hikers who want the satisfaction of summiting a Virunga volcano without extreme physical challenge or exposure anxiety.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The climb begins through thick bamboo forest that forms golden monkey territory. Unlike dedicated golden monkey tracking where you stay with one troop for an hour, the Gahinga trail simply passes through their habitat, offering serendipitous encounters if troops happen to be feeding nearby. As you gain elevation, the vegetation transitions to hagenia-hypericum forest draped in old man's beard lichen.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The summit reward is unique among Virunga climbs: instead of a pointed peak, you reach a crater rim overlooking a swamp-filled caldera. This shallow, vegetation-choked crater holds seasonal water and supports specialized wetland plants, creating habitat distinct from surrounding slopes. The views from the rim are spectacular—on clear days you see other Virunga volcanoes arrayed around you like ancient guardians.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Hike Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-heart', title: 'Most Accessible Summit', description: 'Gentlest Virunga climb—no technical sections or extreme exposure' },
                  { icon: 'fa-droplet', title: 'Unique Crater Swamp', description: 'Summit overlooks water-filled caldera unlike other volcanoes' },
                  { icon: 'fa-leaf', title: 'Bamboo & Golden Monkeys', description: 'Hike through golden monkey habitat with possible sightings' },
                  { icon: 'fa-mountain', title: 'Giant Lobelias', description: 'See otherworldly Afromontane plants above 3,000m' },
                  { icon: 'fa-clock', title: 'Manageable Duration', description: '6 hours total vs 8-10 for Sabinyo or Muhavura' },
                  { icon: 'fa-camera', title: 'Virunga Panoramas', description: 'Summit views encompass neighboring volcanoes and valleys' },
                  { icon: 'fa-users', title: 'Family-Friendly Option', description: 'Suitable for teens and active families with good fitness' },
                  { icon: 'fa-award', title: 'Summit Certificate', description: 'Official documentation of your Virunga volcano achievement' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Sample Climb Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>Note:</strong> This itinerary is tentative and can be customized to suit your preferences and fitness level.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: '08:00 AM', title: 'Briefing & Start', desc: 'Park headquarters orientation, hire porters, begin bamboo forest ascent.' },
                  { time: '09:30 AM', title: 'Hagenia Forest', desc: 'Enter moss-draped woodland zone, steady climbing with breaks.' },
                  { time: '11:00 AM', title: 'Heathland', desc: 'Giant lobelias and groundsels, final push to crater rim.' },
                  { time: '12:00 PM', title: 'Summit!', desc: 'Reach 3,474m crater rim, lunch overlooking swamp caldera.' },
                  { time: '01:00 PM', title: 'Descent', desc: 'Return via same trail, careful footing on steep muddy sections.' },
                  { time: '02:30 PM', title: 'Finish', desc: 'Exit park, receive certificate, celebrate achievement.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time.split(' ')[0]}<br />{step.time.split(' ')[1]}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Common Questions
              </h2>
              <div className="space-y-6">
                {[
                  { q: 'Is Gahinga easier than the others?', a: 'Yes, significantly. Gahinga lacks Sabinyo\'s technical scrambling and is shorter/less steep than Muhavura. Good fitness required but most active hikers manage comfortably.' },
                  { q: 'Will I see golden monkeys?', a: 'Possible but not guaranteed—the trail passes through their habitat. For dedicated viewing, book separate golden monkey tracking which follows a habituated troop.' },
                  { q: 'Can children participate?', a: 'Age 15+ officially, though fit teenagers often manage well. This is the best Virunga option for families with active teens wanting volcano experience.' },
                  { q: 'Should I hire a porter?', a: 'Highly recommended. They carry your daypack and help on steep/muddy sections, making the hike much more enjoyable. Direct community support.' }
                ].map((faq, index) => (
                  <div key={index} className="border-l-4 border-safari-gold pl-6 py-4">
                    <h3 className="text-xl font-bold text-safari-green mb-2">{faq.q}</h3>
                    <p className="text-gray-700">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Mgahinga Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/mount-sabinyo-climb" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800"
                      alt="Mount Sabinyo"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Mount Sabinyo Climb</h3>
                    <p className="text-gray-700 mb-4">The famous "Old Man's Teeth" with 3 peaks.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/golden-monkey-tracking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=800"
                      alt="Golden Monkey"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Golden Monkey Tracking</h3>
                    <p className="text-gray-700 mb-4">Track rare golden monkeys in the bamboo forest.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 6 hours round trip</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Dry seasons</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Moderate</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Small groups</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 15 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Hike
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Park entrance fees",
                    "UWA Ranger guide",
                    "Security escort",
                    "Summit certificate",
                    "Briefing"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Porter (recommended)",
                    "Transport to park HQ",
                    "Packed lunch/water",
                    "Tips for guide/porter"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Moderate. 1,000m elevation gain.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> Altitude may affect some.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Mount Gahinga Volcano Climb"
      />
    </div>
  );
};

export default MountGahingaClimb;
